import type { ApiMode } from '../../shared/api.ts';
import { CHALLENGES, TARGETS, challengeCars, challengeFor, targetFor, type Challenge } from '../data/challenges.ts';
import { characterById } from '../data/characters.ts';
import { allBests } from './bests.ts';

/**
 * Progress on the unlock challenges (data/challenges.ts): for each locked car,
 * the courses its challenge has been done on.
 *
 * On this device, in localStorage, like route progress -- there are no
 * accounts to keep it anywhere else. Not partitioned by sim version: a course
 * done stays done, and an unlocked car stays unlocked, whatever is retuned
 * later.
 */

const KEY = 'idrift.unlocks.v1';

/**
 * Time Attack times are comparable from this sim version on: version 22
 * changed drift scoring only, so a lap driven on 21 is the same lap. Drift
 * scores count only from the version the targets were set on.
 */
const TIMES_COMPARABLE_SINCE = 21;

type Done = Record<string, string[]>;

function read(): Done {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Done) : {};
  } catch {
    return {};
  }
}

function write(done: Done): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(done));
  } catch {
    // Not remembered: this session still counts it.
  }
}

/** The courses a car's challenge has been done on. */
function coursesDone(carId: string, done: Done = read()): string[] {
  const list = done[carId];
  return Array.isArray(list) ? list : [];
}

export interface ChallengeProgress {
  challenge: Challenge;
  /** Courses done, of `challenge.courses`. */
  done: number;
  /** Course ids done. */
  routes: string[];
}

/** A locked car's progress, or null for a car that is not locked (no challenge, or done). */
export function lockOf(carId: string): ChallengeProgress | null {
  const challenge = challengeFor(carId);
  if (!challenge) return null;
  const routes = coursesDone(carId);
  return routes.length >= challenge.courses ? null : { challenge, done: routes.length, routes };
}

export function isLocked(carId: string): boolean {
  return lockOf(carId) !== null;
}

/** One course newly done for one challenge, as it is credited. */
export interface Credit {
  carId: string;
  routeId: string;
  done: number;
  needed: number;
  /** This credit finished the challenge: the car is open. */
  unlocked: boolean;
}

function credit(challenge: Challenge, routeId: string, done: Done): Credit | null {
  const list = coursesDone(challenge.carId, done);
  if (list.length >= challenge.courses || list.includes(routeId)) return null;
  done[challenge.carId] = [...list, routeId];
  const count = list.length + 1;
  return { carId: challenge.carId, routeId, done: count, needed: challenge.courses, unlocked: count >= challenge.courses };
}

export interface FinishedRun {
  routeId: string;
  routeVersion: number;
  carId: string;
  mode: ApiMode;
  simVersion: number;
  points: number;
  timeSeconds: number;
}

/** Whether a run meets a challenge's target on its course. */
function meets(challenge: Challenge, run: FinishedRun): boolean {
  if (!challengeCars(challenge).includes(run.carId)) return false;
  if (challenge.kind === 'drift') {
    if (run.mode !== 'driftRun' || run.simVersion !== TARGETS.simVersion) return false;
    const target = targetFor('drift', run.routeId, run.routeVersion, run.carId);
    return target !== undefined && run.points >= target;
  }
  if (challenge.kind === 'time') {
    if (run.mode !== 'timeAttack' && run.mode !== 'timeAttackPro') return false;
    if (run.simVersion < TIMES_COMPARABLE_SINCE) return false;
    const target = targetFor('time', run.routeId, run.routeVersion, run.carId);
    return target !== undefined && run.timeSeconds <= target;
  }
  return false;
}

/** Credit a finished, ranked run (Drift Run or Time Attack) to every challenge it meets. */
export function creditRun(run: FinishedRun): Credit[] {
  const done = read();
  const credits: Credit[] = [];
  for (const challenge of CHALLENGES) {
    if (!meets(challenge, run)) continue;
    const c = credit(challenge, run.routeId, done);
    if (c) credits.push(c);
  }
  if (credits.length > 0) write(done);
  return credits;
}

/** Credit a tandem battle won against a house driver of this skill, in this car. */
export function creditBattleWin(routeId: string, carId: string, rivalSkill: number): Credit[] {
  const done = read();
  const credits: Credit[] = [];
  for (const challenge of CHALLENGES) {
    if (challenge.kind !== 'tandem' || !challenge.rival) continue;
    if (!challengeCars(challenge).includes(carId)) continue;
    if (rivalSkill < characterById(challenge.rival).skill) continue;
    const c = credit(challenge, routeId, done);
    if (c) credits.push(c);
  }
  if (credits.length > 0) write(done);
  return credits;
}

/**
 * Credit the bests already on this device: anyone who has already driven a
 * target starts with it counted. Run at every start; a best already counted
 * counts once. Tandem wins were never recorded with their car, so they
 * cannot be credited.
 */
export function creditPastRuns(): Credit[] {
  return allBests().flatMap((b) => creditRun(b));
}

/** How a challenge reads in the garage and on the course screen. */
export function describe(challenge: Challenge, categoryName: string): string {
  const where = `${challenge.courses} courses, in a ${categoryName} car`;
  if (challenge.kind === 'drift') return `Score the target in Drift Run on ${where}.`;
  if (challenge.kind === 'time') return `Beat the target time in Time Attack on ${where}.`;
  const rival = challenge.rival ? characterById(challenge.rival).name : 'the house';
  return `Beat ${rival} in Drift Tandem on ${where}.`;
}
